import type { LoomAuthoringDocument } from "../features/parametric-geometry/loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "../features/parametric-geometry/loomAuthoringHistory";
import { canonicalLoomDefinitionJson } from "../features/parametric-geometry/loomAcceptedDefinitionBinding";
import {
  type LoomRebuildFuselageStation,
  type LoomRebuildNaca4,
  type LoomWingBodyDefinition,
} from "../features/parametric-geometry/loomAuthoritativeDefinition";
import {
  verifyLoomNativeHybridAssetBuffer,
  type LoomNativeHybridAsset,
  type LoomVerifiedNativeHybridAsset,
} from "../features/parametric-geometry/loomNativeHybridAsset";
import { deriveLoomPresetV3Definition } from "../features/parametric-geometry/loomPresetV3Binding";
import { apiClient, getAuthToken } from "./client";
import {
  parseLoomAuthoritativeRebuildRequestV3,
  type LoomWingBodyDefinitionV2,
} from "./loomRebuildV3";
import {
  deriveLoomAircraftOccurrenceRebuildDefinition,
  LOOM_REBUILD_EVALUATOR_V4,
  LOOM_REBUILD_SCHEMA_V4,
  LOOM_REBUILD_TOPOLOGY_V4,
  parseLoomAuthoritativeRebuildRequestV4,
  type LoomAuthoritativeRebuildRequestV4,
} from "./loomRebuildV4";
import {
  LOOM_OCCURRENCE_ACCEPTED_SCHEMA,
  LOOM_OCCURRENCE_SOURCE_AUTHORITY,
  LOOM_OCCURRENCE_UNION_ALGORITHM,
} from "../features/parametric-geometry/loomAircraftOccurrenceAssetContract";
import {
  parseLoomBooleanConstructionTrace,
  type LoomBooleanConstructionTrace,
} from "./loomRebuildConstructionTrace";
import {
  verifyLoomAircraftOccurrenceAssetBuffer,
  type LoomVerifiedAircraftOccurrenceAsset,
} from "../features/parametric-geometry/loomAircraftOccurrenceAsset";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
export const LOOM_REBUILD_EVALUATOR_V1 = "loom.canonical-wing-body/v1" as const;
export const LOOM_REBUILD_EVALUATOR = "loom.native-wing-body/v2" as const;
export const LOOM_REBUILD_EVALUATOR_V3 = "loom.native-wing-body/v3" as const;
export const LOOM_REBUILD_TOPOLOGY = "canonical-starboard-wing-fuselage-union" as const;
export const LOOM_REBUILD_TOPOLOGY_V3 = "selected-wing-fuselage-pairwise-union" as const;

export interface LoomAuthoritativeRebuildRequestV1 {
  schema: "loom.authoritative-rebuild-request/v1";
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR_V1;
  topology: typeof LOOM_REBUILD_TOPOLOGY;
  parameters: {
    sweep_deg: number; taper: number; chord_root: number; half_span: number;
    twist_tip_deg: number; dihedral_deg: number; te_thickness: number;
  };
}

export type { LoomRebuildFuselageStation, LoomRebuildNaca4, LoomWingBodyDefinition };

export interface LoomAuthoritativeRebuildRequestV2 {
  schema: "loom.authoritative-rebuild-request/v2";
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR;
  topology: typeof LOOM_REBUILD_TOPOLOGY;
  definition: LoomWingBodyDefinition;
}

export interface LoomAuthoritativeRebuildRequestV3 {
  schema: "loom.authoritative-rebuild-request/v3";
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR_V3;
  topology: typeof LOOM_REBUILD_TOPOLOGY_V3;
  definition: LoomWingBodyDefinitionV2;
}

export type { LoomWingBodyDefinitionV2 } from "./loomRebuildV3";
export type { LoomAuthoritativeRebuildRequestV4 } from "./loomRebuildV4";

export type LoomAuthoritativeRebuildRequest =
  | LoomAuthoritativeRebuildRequestV1
  | LoomAuthoritativeRebuildRequestV2
  | LoomAuthoritativeRebuildRequestV3
  | LoomAuthoritativeRebuildRequestV4;

export const LOOM_REBUILD_PHASES = [
  "preparing",
  "evaluating_parametric_surfaces",
  "implicit_guided_trimming",
  "panelization_qc",
  "packing_verification",
  "complete",
] as const;

export type LoomRebuildPhase = typeof LOOM_REBUILD_PHASES[number];

export interface LoomRebuildProgress {
  request_id: string;
  source_revision: number;
  source_state_id: string;
  definition_revision: string | null;
  phase: LoomRebuildPhase;
  phase_index: number;
  phase_count: 6;
  message: string;
  candidate_index: number | null;
  candidate_count: number | null;
  elapsed_ms: number;
  construction_trace: LoomBooleanConstructionTrace | null;
}

export interface LoomRebuildStatus {
  configured: boolean;
  configuration_mode: "explicit" | "same-repository-local" | "unavailable";
  worker_state: "cold" | "warm" | "unavailable";
  detail: string;
  last_build_ms: number | null;
  cold_start_estimate_ms: number;
  warm_rebuild_estimate_ms: number;
  current_progress: LoomRebuildProgress | null;
}

export class LoomAuthoritativeRebuildError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly phase: LoomRebuildPhase | null,
  ) {
    super(message);
    this.name = "LoomAuthoritativeRebuildError";
  }
}

export interface LoomAuthoritativeRebuildResult {
  asset: LoomVerifiedNativeHybridAsset | null;
  assemblyAsset?: LoomVerifiedAircraftOccurrenceAsset | null;
  buildMs: number;
  definitionRevision: string | null;
  sourceRevision: number;
  sourceStateId: string;
}

export type LoomRebuildBlockerCode =
  | "document-identity"
  | "fixed-definition"
  | "graph-structure"
  | "parameter-out-of-range"
  | "parameter-unbound"
  | "preset-binding"
  | "wing-definition";

export interface LoomRebuildBlocker {
  code: LoomRebuildBlockerCode;
  controlPath: string | null;
  message: string;
  nodeId: string | null;
}

export type LoomRebuildDefinition =
  | { kind: "available"; request: LoomAuthoritativeRebuildRequestV2 | LoomAuthoritativeRebuildRequestV3 | LoomAuthoritativeRebuildRequestV4 }
  | { blockers: readonly LoomRebuildBlocker[]; kind: "unavailable"; reason: string };

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && new Set(left).size === left.length
    && left.every((value) => right.includes(value));
}

function blocker(
  code: LoomRebuildBlockerCode,
  message: string,
  nodeId: string | null = null,
  controlPath: string | null = null,
): LoomRebuildBlocker {
  return { code, controlPath, message, nodeId };
}

export function deriveLoomAuthoritativeRebuildRequest(
  _asset: LoomNativeHybridAsset,
  document: LoomAuthoringDocument,
  requestId: string,
): LoomRebuildDefinition {
  if (document.presetBinding) {
    const blockers: LoomRebuildBlocker[] = [];
    if (!/^[A-Za-z0-9._:-]{1,160}$/.test(document.documentId)) blockers.push(blocker(
      "document-identity", "the Pattern document identity is invalid"));
    let definition: LoomWingBodyDefinitionV2 | null = null;
    try {
      definition = deriveLoomPresetV3Definition(document);
    } catch (error: unknown) {
      blockers.push(blocker(
        "preset-binding",
        error instanceof Error ? error.message : "the persisted v3 preset binding is invalid",
      ));
    }
    if (!definition || blockers.length) return {
      blockers, kind: "unavailable", reason: `Not rebuildable: ${blockers.map((item) => item.message).join("; ")}.`,
    };
    const request: LoomAuthoritativeRebuildRequestV3 = {
      schema: "loom.authoritative-rebuild-request/v3", requestId,
      sourceDefinition: {
        documentId: document.documentId, revision: document.revision,
        stateId: loomAuthoringDocumentStateId(document),
      },
      evaluatorId: LOOM_REBUILD_EVALUATOR_V3,
      topology: LOOM_REBUILD_TOPOLOGY_V3,
      definition,
    };
    parseLoomAuthoritativeRebuildRequestV3(request);
    return { kind: "available", request };
  }
  const definition = deriveLoomAircraftOccurrenceRebuildDefinition(document);
  const blockers: LoomRebuildBlocker[] = [];
  if (!definition) blockers.push(blocker(
    "graph-structure",
    "the geometry engine could not build every wing and fuselage occurrence in this aircraft; resolve unsupported transforms, disconnected parts, or unsupported section definitions first",
    null,
  ));
  if (!/^[A-Za-z0-9._:-]{1,160}$/.test(document.documentId)) blockers.push(blocker(
    "document-identity", "the Pattern document identity is invalid"));
  if (!definition || blockers.length) return {
    blockers, kind: "unavailable", reason: `Not rebuildable: ${blockers.map((item) => item.message).join("; ")}.`,
  };
  const request: LoomAuthoritativeRebuildRequestV4 = {
    schema: LOOM_REBUILD_SCHEMA_V4, requestId,
    sourceDefinition: { documentId: document.documentId, revision: document.revision, stateId: loomAuthoringDocumentStateId(document) },
    evaluatorId: LOOM_REBUILD_EVALUATOR_V4, topology: LOOM_REBUILD_TOPOLOGY_V4,
    definition,
  };
  parseLoomAuthoritativeRebuildRequestV4(request);
  return { kind: "available", request };
}

async function responseFailure(response: Response): Promise<LoomAuthoritativeRebuildError> {
  try {
    const value = record(await response.clone().json(), "Loom rebuild failure");
    const reason = String(value.reason ?? value.detail ?? response.statusText);
    const code = typeof value.code === "string" && value.code.trim() ? value.code : null;
    const phase = LOOM_REBUILD_PHASES.includes(value.phase as LoomRebuildPhase)
      ? value.phase as LoomRebuildPhase : null;
    return new LoomAuthoritativeRebuildError(
      `Authoritative rebuild ${response.status}: ${reason}`, response.status, code, phase,
    );
  } catch (error: unknown) {
    if (error instanceof LoomAuthoritativeRebuildError) return error;
    const reason = await response.text().catch(() => response.statusText);
    return new LoomAuthoritativeRebuildError(
      `Authoritative rebuild ${response.status}: ${reason || response.statusText}`,
      response.status, null, null,
    );
  }
}

function record(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${label} is not an object`);
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], label: string): void {
  if (!sameSet(Object.keys(value), keys)) throw new Error(`${label} has an unsupported field set`);
}

function nonnegative(value: unknown, label: string, integer = false): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || (integer && !Number.isInteger(value))) {
    throw new Error(`${label} is invalid`);
  }
  return value;
}

export function parseLoomRebuildStatus(value: unknown): LoomRebuildStatus {
  const item = record(value, "Loom rebuild status");
  const baseKeys = ["configured", "configuration_mode", "worker_state", "detail", "last_build_ms", "cold_start_estimate_ms", "warm_rebuild_estimate_ms"];
  exactKeys(item, "current_progress" in item ? [...baseKeys, "current_progress"] : baseKeys, "Loom rebuild status");
  const modes = ["explicit", "same-repository-local", "unavailable"] as const;
  const states = ["cold", "warm", "unavailable"] as const;
  if (typeof item.configured !== "boolean" || !modes.includes(item.configuration_mode as typeof modes[number])
      || !states.includes(item.worker_state as typeof states[number]) || typeof item.detail !== "string" || !item.detail.trim()) {
    throw new Error("Loom rebuild status fields are invalid");
  }
  const last = item.last_build_ms === null ? null : nonnegative(item.last_build_ms, "last_build_ms");
  const configured = item.configured;
  if (configured
    ? item.configuration_mode === "unavailable" || item.worker_state === "unavailable"
    : item.configuration_mode !== "unavailable" || item.worker_state !== "unavailable") {
    throw new Error("Loom rebuild status availability is inconsistent");
  }
  let currentProgress: LoomRebuildProgress | null = null;
  if (item.current_progress !== undefined && item.current_progress !== null) {
    const progress = record(item.current_progress, "Loom rebuild progress");
    exactKeys(progress, ["request_id", "source_revision", "source_state_id", "definition_revision",
      "phase", "phase_index", "phase_count", "message", "candidate_index", "candidate_count",
      "elapsed_ms", "construction_trace"], "Loom rebuild progress");
    const phase = progress.phase as LoomRebuildPhase;
    const phaseIndex = nonnegative(progress.phase_index, "phase_index", true);
    const sourceRevision = nonnegative(progress.source_revision, "source_revision", true);
    const candidateIndex = progress.candidate_index === null
      ? null : nonnegative(progress.candidate_index, "candidate_index", true);
    const candidateCount = progress.candidate_count === null
      ? null : nonnegative(progress.candidate_count, "candidate_count", true);
    if (typeof progress.request_id !== "string" || !progress.request_id.trim()
        || typeof progress.source_state_id !== "string" || !progress.source_state_id.trim()
        || (progress.definition_revision !== null
          && (typeof progress.definition_revision !== "string" || !progress.definition_revision.trim()))
        || !LOOM_REBUILD_PHASES.includes(phase) || phaseIndex !== LOOM_REBUILD_PHASES.indexOf(phase)
        || progress.phase_count !== LOOM_REBUILD_PHASES.length
        || typeof progress.message !== "string" || !progress.message.trim()
        || (candidateIndex === null) !== (candidateCount === null)
        || (phase === "implicit_guided_trimming" && candidateIndex === null)
        || (candidateIndex !== null && (phase !== "implicit_guided_trimming"
          || candidateIndex < 1 || candidateCount === null || candidateCount > 4
          || candidateCount < candidateIndex))) {
      throw new Error("Loom rebuild progress fields are invalid");
    }
    currentProgress = {
      request_id: progress.request_id, source_revision: sourceRevision,
      source_state_id: progress.source_state_id,
      definition_revision: progress.definition_revision as string | null,
      phase, phase_index: phaseIndex, phase_count: 6, message: progress.message,
      candidate_index: candidateIndex, candidate_count: candidateCount,
      elapsed_ms: nonnegative(progress.elapsed_ms, "elapsed_ms"),
      construction_trace: progress.construction_trace === null
        ? null : parseLoomBooleanConstructionTrace(progress.construction_trace),
    };
  }
  return {
    configured,
    configuration_mode: item.configuration_mode as LoomRebuildStatus["configuration_mode"],
    worker_state: item.worker_state as LoomRebuildStatus["worker_state"],
    detail: item.detail, last_build_ms: last,
    cold_start_estimate_ms: nonnegative(item.cold_start_estimate_ms, "cold_start_estimate_ms", true),
    warm_rebuild_estimate_ms: nonnegative(item.warm_rebuild_estimate_ms, "warm_rebuild_estimate_ms", true),
    current_progress: currentProgress,
  };
}

export async function requestLoomAuthoritativeRebuild(
  projectId: string,
  request: LoomAuthoritativeRebuildRequest,
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<LoomAuthoritativeRebuildResult> {
  let parsedRequestV4: LoomAuthoritativeRebuildRequestV4 | null = null;
  if (request.schema === "loom.authoritative-rebuild-request/v3") {
    parseLoomAuthoritativeRebuildRequestV3(request);
  } else if (request.schema === LOOM_REBUILD_SCHEMA_V4) {
    parsedRequestV4 = parseLoomAuthoritativeRebuildRequestV4(request);
  }
  const token = await getAuthToken();
  const response = await fetcher(`${API_BASE}/thread/v1/projects/${encodeURIComponent(projectId)}/aero/loom/rebuilds`, {
    method: "POST", credentials: "include", signal,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await responseFailure(response);
  const requiredHeader = (name: string) => {
    const value = response.headers.get(name);
    if (value === null || !value.trim()) throw new Error(`Authoritative rebuild response is missing ${name}`);
    return value;
  };
  const requestId = requiredHeader("X-Loom-Request-Id");
  const acceptedTopology = requiredHeader("X-Loom-Accepted-Topology");
  const sourceRevision = Number(requiredHeader("X-Loom-Source-Revision"));
  const sourceStateId = requiredHeader("X-Loom-Source-State-Id");
  const buildMs = Number(requiredHeader("X-Loom-Build-Ms"));
  const assetVerification = requiredHeader("X-Loom-Asset-Verification");
  if (requestId !== request.requestId || acceptedTopology !== request.topology
      || sourceRevision !== request.sourceDefinition.revision
      || sourceStateId !== request.sourceDefinition.stateId
      || assetVerification !== "structural-worker-frame") {
    throw new Error("Authoritative rebuild response is stale or bound to another Pattern head");
  }
  if (request.schema === "loom.authoritative-rebuild-request/v3"
      && (requiredHeader("X-Loom-Accepted-Schema") !== "loom.accepted-mesh/v3"
        || requiredHeader("X-Loom-Evaluator-Id") !== request.evaluatorId
        || requiredHeader("X-Loom-Union-Algorithm-Id")
          !== "loom.implicit-guided-explicit-union/v1")) {
    throw new Error("Authoritative rebuild v3 response marker is unsupported");
  }
  if (!Number.isFinite(buildMs) || buildMs < 0) throw new Error("Authoritative rebuild timing is invalid");
  const buffer = await response.arrayBuffer();
  if (request.schema === LOOM_REBUILD_SCHEMA_V4) {
    if (!response.headers.get("Content-Type")?.startsWith("application/vnd.loom.aircraft-occurrence-surface")
        || requiredHeader("X-Loom-Accepted-Schema") !== LOOM_OCCURRENCE_ACCEPTED_SCHEMA
        || requiredHeader("X-Loom-Evaluator-Id") !== request.evaluatorId
        || requiredHeader("X-Loom-Union-Algorithm-Id") !== LOOM_OCCURRENCE_UNION_ALGORITHM
        || requiredHeader("X-Loom-Source-Authority") !== LOOM_OCCURRENCE_SOURCE_AUTHORITY) {
      throw new Error("Authoritative rebuild v4 response authority is unsupported");
    }
    const assemblyAsset = await verifyLoomAircraftOccurrenceAssetBuffer(buffer);
    const header = assemblyAsset.header; const binding = header.definitionBinding;
    const definitionRevision = requiredHeader("X-Loom-Definition-Revision");
    if (requiredHeader("X-Loom-Geometry-Revision") !== header.surface.geometryRevision
        || requiredHeader("X-Loom-Topology-Revision") !== header.surface.topologyRevision
        || definitionRevision !== binding.definitionRevision
        || requiredHeader("X-Loom-Assembly-Topology-Revision") !== binding.assemblyTopologyRevision
        || requiredHeader("X-Loom-Pose-Revision") !== binding.poseRevision
        || requiredHeader("X-Loom-Scene-Revision") !== header.cacheReceipt.sceneRevision
        || Number(requiredHeader("X-Loom-Union-Group-Count")) !== header.unionGroups.length
        || canonicalLoomDefinitionJson(binding.definition)
          !== canonicalLoomDefinitionJson(parsedRequestV4!.definition)) {
      throw new Error("Authoritative rebuild v4 asset is not bound to the requested aircraft head");
    }
    return { asset: null, assemblyAsset, buildMs, definitionRevision, sourceRevision, sourceStateId };
  }
  const asset = await verifyLoomNativeHybridAssetBuffer(buffer);
  const accepted = asset.header.acceptedGeometry;
  if (requiredHeader("X-Loom-Geometry-Revision") !== accepted.geometryRevision
      || requiredHeader("X-Loom-Topology-Revision") !== accepted.topologyRevision) {
    throw new Error("Authoritative rebuild headers disagree with the accepted asset revisions");
  }
  let definitionRevision: string | null = null;
  if (request.schema === "loom.authoritative-rebuild-request/v2"
      || request.schema === "loom.authoritative-rebuild-request/v3") {
    definitionRevision = requiredHeader("X-Loom-Definition-Revision");
    const binding = accepted.definitionBinding;
    const acceptedSchema = request.schema === "loom.authoritative-rebuild-request/v3"
      ? "loom.accepted-mesh/v3" : "loom.accepted-mesh/v2";
    if (accepted.schema !== acceptedSchema || !binding
        || binding.evaluatorId !== request.evaluatorId
        || binding.definitionRevision !== definitionRevision
        || asset.header.parameters.definitionRevision !== definitionRevision
        || canonicalLoomDefinitionJson(binding.definition)
          !== canonicalLoomDefinitionJson(request.definition)
        || canonicalLoomDefinitionJson(asset.header.parameters.authoritativeDefinition)
          !== canonicalLoomDefinitionJson(request.definition)) {
      throw new Error("Authoritative rebuild asset is not bound to the requested versioned definition");
    }
    if (request.schema === "loom.authoritative-rebuild-request/v3") {
      const union = accepted.unionReceipt;
      if (!union || union.evaluatorId !== request.evaluatorId
          || union.topologyId !== request.topology
          || union.definitionRevision !== definitionRevision
          || requiredHeader("X-Loom-Surface-Geometry-Revision")
            !== union.surfaceGeometryRevision
          || requiredHeader("X-Loom-Surface-Topology-Revision")
            !== union.surfaceTopologyRevision
          || requiredHeader("X-Loom-Kernel-Topology-Revision")
            !== union.kernelTopologyRevision) {
        throw new Error("Authoritative rebuild v3 surface receipt disagrees with its request or response markers");
      }
    }
  } else if (accepted.schema !== "loom.accepted-mesh/v1" || accepted.definitionBinding) {
    throw new Error("Legacy authoritative rebuild returned a non-v1 accepted asset");
  }
  return { asset, buildMs, definitionRevision, sourceRevision, sourceStateId };
}

export async function fetchLoomRebuildStatus(projectId: string, signal?: AbortSignal): Promise<LoomRebuildStatus> {
  return parseLoomRebuildStatus(await apiClient<unknown>(
    `/thread/v1/projects/${encodeURIComponent(projectId)}/aero/loom/rebuilds/status`, { signal },
  ));
}
